import { useState, type ReactNode } from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  pointerWithin,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import { GripVertical } from 'lucide-react'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import {
  EDITORIAL_GRID_PRESET_CONFIG,
  type EditorialGridLayoutPreset,
} from '../page-blocks/definitions/editorialGrid'
import { PresetVisual } from './PresetVisual'
import { BlockPreview } from './BlockPreview'
import type { BlockCanvasState } from './useBlockCanvas'
import type { PageBlockType } from '../../api/types'

// Drag ID schemes:
//   shelf:TYPE                 — regular block from shelf
//   shelf:editorial-grid:PRESET — editorial-grid preset from shelf
//   UUID                       — canvas slot being reordered

type ActiveShelf =
  | { kind: 'block'; type: PageBlockType }
  | { kind: 'hrow'; preset: EditorialGridLayoutPreset }

type ActiveArticle = { articleId: string; title: string }

interface BlockEditorDndContextProps {
  canvas: BlockCanvasState
  onArticleDrop?: (slotId: string, articleId: string) => void
  children: ReactNode
}

export function BlockEditorDndContext({ canvas, onArticleDrop, children }: BlockEditorDndContextProps) {
  const [activeShelf, setActiveShelf] = useState<ActiveShelf | null>(null)
  const [activeArticle, setActiveArticle] = useState<ActiveArticle | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )

  function handleDragStart(event: DragStartEvent) {
    const id = String(event.active.id) // this is a UUID from the shelf button
    const data = event.active.data.current as { kind: string; type?: PageBlockType; preset?: EditorialGridLayoutPreset; articleId?: string; title?: string } | undefined

    if (data?.kind === 'hrow' && data.preset) {
      const preset = data.preset
      setActiveShelf({ kind: 'hrow', preset })
      canvas.insertSlot({
        id,
        type: 'editorial-grid',
        order: canvas.slots.length,
        config: { layoutPreset: preset, column1: null, column2: null, column3: null },
      })
    } else if (data?.kind === 'block' && data.type) {
      const type = data.type
      setActiveShelf({ kind: 'block', type })
      canvas.insertSlot({
        id,
        type,
        order: canvas.slots.length,
        config: { ...PAGE_BLOCK_REGISTRY[type].defaultConfig },
      })
    } else if (data?.kind === 'article') {
      setActiveArticle({ articleId: data.articleId as string, title: data.title as string })
      // article drags don't insert provisional slots
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    const activeId = String(active.id)
    const wasShelfDrag = activeShelf !== null
    setActiveShelf(null)
    // ── Article drop ─────────────────────────────────────────────────────────────────────────
    if (activeArticle) {
      setActiveArticle(null)
      if (!over) return
      const overId = String(over.id)
      const parts = overId.split(':')

      if (parts.length === 3) {
        // Column content key: parentSlotId:colKey:colBlockId
        const [parentSlotId, colKey, colBlockId] = parts
        const parentSlot = canvas.slots.find((s) => s.id === parentSlotId)
        if (!parentSlot) return
        const colBlock = parentSlot.columnBlocks?.[colKey as import('../editor-shared/canvasTypes').ColKey]?.find((b) => b.id === colBlockId)
        if (!colBlock) return
        const def = PAGE_BLOCK_REGISTRY[colBlock.type]
        if (!def || def.category !== 'editorial' || def.maxArticles === 0) return
        onArticleDrop?.(overId, activeArticle.articleId)
        return
      }

      // Top-level canvas slot
      const targetSlot = canvas.slots.find((s) => s.id === overId)
      if (!targetSlot) return
      const def = PAGE_BLOCK_REGISTRY[targetSlot.type]
      if (!def || def.category !== 'editorial' || def.maxArticles === 0) return
      onArticleDrop?.(overId, activeArticle.articleId)
      return
    }
    // ── Shelf drag end ───────────────────────────────────────────────────────
    // The provisional slot is already in the canvas with its final UUID.
    // Just position it correctly (reorder) or remove it on cancel.
    if (wasShelfDrag) {
      if (!over) {
        canvas.removeSlotById(activeId)
        return
      }
      const overId = String(over.id)
      if (overId !== activeId) {
        const oldIndex = canvas.slots.findIndex((s) => s.id === activeId)
        const newIndex = canvas.slots.findIndex((s) => s.id === overId)
        if (oldIndex !== -1 && newIndex !== -1) {
          canvas.reorderBlocks(oldIndex, newIndex)
        }
      }
      return
    }

    // ── Canvas row reorder ──────────────────────────────────────────────────
    if (!over || activeId === String(over.id)) return
    const oldIndex = canvas.slots.findIndex((s) => s.id === activeId)
    const newIndex = canvas.slots.findIndex((s) => s.id === String(over.id))
    if (oldIndex !== -1 && newIndex !== -1) {
      canvas.reorderBlocks(oldIndex, newIndex)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={activeArticle ? pointerWithin : closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {children}

      <DragOverlay dropAnimation={null}>
        {activeArticle ? (
          <div className="flex w-64 items-center gap-2 rounded border border-line-strong bg-canvas px-3 py-2 shadow-xl">
            <GripVertical size={12} className="shrink-0 text-fg-secondary opacity-40" aria-hidden="true" />
            <span className="truncate text-[11px] font-semibold text-fg">{activeArticle.title}</span>
          </div>
        ) : activeShelf?.kind === 'block' ? (
          <div
            className="w-80 origin-top-left overflow-hidden rounded border border-line-strong shadow-xl"
            style={{ transform: 'scale(0.55)' }}
          >
            <div className="bg-surface px-3 py-1.5 text-xs font-semibold text-fg border-b border-line">
              {PAGE_BLOCK_REGISTRY[activeShelf.type].label}
            </div>
            <BlockPreview type={activeShelf.type} config={PAGE_BLOCK_REGISTRY[activeShelf.type].defaultConfig} />
          </div>
        ) : activeShelf?.kind === 'hrow' ? (
          <div className="w-64 rounded border border-line-strong bg-canvas px-3 py-2 shadow-xl">
            <PresetVisual gridTemplate={EDITORIAL_GRID_PRESET_CONFIG[activeShelf.preset].gridTemplate} />
            <div className="text-xs font-semibold text-fg">
              {EDITORIAL_GRID_PRESET_CONFIG[activeShelf.preset].label}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
